import type { TFunction } from 'i18next';
import { PUBLISH_ERROR_COPY, publishErrorCopy, type PublishErrorCopy } from '@unipostudio/shared-types';
import { PLATFORM_LIST } from '@/shared/components/platform-list';

/**
 * A failed channel result in the reader's words: a title, a one-line reason
 * and one action, from the code the worker recorded (PUBLISH_ERROR_COPY in
 * shared-types). The channel's own message, which is English, is kept for
 * "Details" only and is never the main text.
 */
export interface PublishFailureText {
  copy: PublishErrorCopy;
  title: string;
  reason: string;
  /** The channel's message, without the worker's `[PLATFORM]` prefix; null when there is none. */
  details: string | null;
}

export interface FailedResultLike {
  platform: string;
  errorCode?: string | null;
  errorMessage?: string | null;
}

/** A platform as the API names it (INSTAGRAM, GOOGLE_BUSINESS), in the app's own words. */
export function platformName(platform: string): string {
  return PLATFORM_LIST.find((p) => p.id === platform.toLowerCase())?.label ?? platform;
}

/**
 * Results recorded before the worker kept the channel's code have only the
 * English message. The few messages the app recognised then still get
 * their words.
 */
function legacyCopy(message: string): PublishErrorCopy | null {
  const lower = message.toLowerCase();
  if (lower.includes('token expired') || lower.includes('token_expired') || lower.includes('could not be renewed')) return PUBLISH_ERROR_COPY['TOKEN_EXPIRED'] ?? null;
  if (lower.includes('did not confirm the post')) return PUBLISH_ERROR_COPY['PUBLISH_OUTCOME_UNKNOWN'] ?? null;
  if (lower.includes('could not record it')) return PUBLISH_ERROR_COPY['POSTED_NOT_RECORDED'] ?? null;
  return null;
}

/** `[INSTAGRAM] Media expired` → `Media expired`. */
function stripPrefix(message: string): string {
  return message.replace(/^\[[A-Z_]+\]\s*/, '').trim();
}

/** The copy of a failed result: its code's, else (an old result) its recognised message's, else the fallback. */
export function failureCopy(result: FailedResultLike): PublishErrorCopy {
  const copy = publishErrorCopy(result.platform, result.errorCode);
  const message = result.errorMessage?.trim();
  if (copy.copy === 'unknown' && !result.errorCode && message) return legacyCopy(message) ?? copy;
  return copy;
}

export function describePublishFailure(result: FailedResultLike, t: TFunction): PublishFailureText {
  const message = result.errorMessage?.trim() || null;
  const copy = failureCopy(result);
  const params = { platform: platformName(result.platform) };
  return {
    copy,
    title: String(t(copy.titleKey, params)),
    reason: String(t(copy.reasonKey, params)),
    details: message ? stripPrefix(message) : null,
  };
}

/** A post deleted from its platform keeps a FAILED result: that is not a failure. */
export function isFailedResult(r: { status: string; errorCode?: string | null; errorMessage?: string | null }): boolean {
  return r.status === 'FAILED' && r.errorCode !== 'DELETED_FROM_PLATFORM' && r.errorMessage !== 'Deleted from platform';
}

/**
 * Whether Retry may be offered for a post. Retry (POST /posts/:id/retry)
 * sends every failed channel again, so one channel that may already have the
 * post makes Retry unsafe for the whole post: it could publish it twice there.
 */
export function postRetryIsSafe(results: ReadonlyArray<FailedResultLike & { status: string; platformPostId?: string | null }> | null | undefined): boolean {
  // A failure that still carries a post id went out in part (an X thread
  // that broke, a Telegram album sent in pieces): Retry would send that part
  // again, so the post is duplicated instead (F18).
  return !(results ?? []).some((r) => isFailedResult(r) && (failureCopy(r).mayBeLive || !!r.platformPostId));
}
